import { MessageCircle, AlertTriangle } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CITIES, OTHER_CITY, waLink } from "../../lib/data";
import { fieldCls, fmtDate, isShortNotice } from "../../lib/pricing";
import { DateCalendar } from "./DateCalendar";

export const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">{label}</Label>
    {children}
  </div>
);

export const StepEvent = ({ form, set, unavailable = [] }) => (
  <div className="space-y-6" data-testid="checkout-step-2">
    <div className="grid sm:grid-cols-[auto_1fr] gap-5">
      <Field label="Data do evento">
        <DateCalendar testid="checkout-step-2-event-date" value={form.eventDate} onChange={(v) => set("eventDate", v)} disabledDates={unavailable} />
        <p className="text-xs text-zinc-400">Datas riscadas já estão reservadas ou indisponíveis.</p>
      </Field>
      <div className="space-y-5">
        <div className="rounded-2xl border border-white/10 p-5" data-testid="selected-date-display">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Data escolhida</p>
          <p className="font-display font-bold text-xl text-white mt-1">{form.eventDate ? fmtDate(form.eventDate) : "Selecione no calendário"}</p>
        </div>
        <Field label="Horário de início">
          <Input data-testid="checkout-step-2-event-time" type="time" value={form.eventTime}
            onChange={(e) => set("eventTime", e.target.value)} className={fieldCls} />
        </Field>
      </div>
    </div>
    {isShortNotice(form.eventDate, form.eventTime) && (
      <div className="rounded-2xl border border-[#FFD200]/40 bg-[#FFD200]/10 p-5" data-testid="short-notice-warning">
        <p className="flex items-start gap-3 text-sm text-zinc-200"><AlertTriangle className="w-5 h-5 text-[#FFD200] shrink-0" />
          Eventos com menos de 48 horas de antecedência precisam ter a disponibilidade confirmada pelo WhatsApp antes da reserva.</p>
        <a href={waLink(`Olá! Quero confirmar a disponibilidade da máquina para ${fmtDate(form.eventDate)}${form.eventTime ? ` às ${form.eventTime}` : ""}.`)} target="_blank" rel="noreferrer"
          data-testid="short-notice-whatsapp" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold">
          <MessageCircle className="w-4 h-4" /> Confirmar disponibilidade no WhatsApp
        </a>
      </div>
    )}
    <Field label="Cidade">
      <Select value={form.city} onValueChange={(v) => set("city", v)}>
        <SelectTrigger data-testid="checkout-step-2-city-select" className={fieldCls}><SelectValue placeholder="Selecione a cidade" /></SelectTrigger>
        <SelectContent className="bg-[#111219] border-white/10 text-white">
          {[...CITIES, OTHER_CITY].map((c) => (
            <SelectItem key={c} value={c} data-testid={`city-option-${c}`} className="focus:bg-[#FFD200] focus:text-[#08080C]">{c}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
    {form.city === OTHER_CITY ? (
      <div className="rounded-2xl border border-[#FFD200]/40 bg-[#FFD200]/10 p-5" data-testid="other-city-notice">
        <p className="flex items-start gap-3 text-sm text-zinc-200"><AlertTriangle className="w-5 h-5 text-[#FFD200] shrink-0" />
          Os valores on-line valem para a Grande Vitória. Para outras cidades do ES, fale com a gente para calcular o deslocamento.</p>
        <a href={waLink("Olá! Quero um orçamento da máquina de pelúcias para um evento fora da Grande Vitória.")} target="_blank" rel="noreferrer"
          data-testid="other-city-whatsapp" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold">
          <MessageCircle className="w-4 h-4" /> Consultar no WhatsApp
        </a>
      </div>
    ) : (
      <>
        <Field label="Endereço completo do evento">
          <Textarea data-testid="checkout-step-2-address" rows={3} value={form.address} placeholder="Rua, número, bairro, complemento"
            onChange={(e) => set("address", e.target.value)} className={`${fieldCls} h-auto py-3`} />
        </Field>
        <div className="grid sm:grid-cols-[1fr_180px] gap-5">
          <Field label="Nome do local do evento">
            <Input data-testid="checkout-step-2-venue" value={form.venueName} placeholder="Ex.: Salão de festas, buffet, residência"
              onChange={(e) => set("venueName", e.target.value)} className={fieldCls} />
          </Field>
          <Field label="Nº estimado de convidados">
            <Input data-testid="checkout-step-2-guests" type="number" min={1} value={form.guests}
              onChange={(e) => set("guests", e.target.value)} className={fieldCls} />
          </Field>
        </div>
        <Field label="Condições de acesso ao local de instalação">
          <Textarea data-testid="checkout-step-2-access" rows={3} value={form.accessDetails}
            placeholder="Pavimento da instalação, escadas, degraus, rampas, elevadores, corredores estreitos e restrições do local"
            onChange={(e) => set("accessDetails", e.target.value)} className={`${fieldCls} h-auto py-3`} />
          <p className="text-xs text-zinc-400">A máquina pesa cerca de 170 kg e mede aproximadamente 2 m de altura por 1 m de largura. Não é transportada por escadas ou degraus.</p>
        </Field>
      </>
    )}
  </div>
);

export const StepClient = ({ form, set }) => (
  <div className="space-y-5" data-testid="checkout-step-3">
    <Field label="Nome completo">
      <Input data-testid="checkout-step-3-client-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls} placeholder="Seu nome" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-5">
      <Field label="CPF ou CNPJ">
        <Input data-testid="checkout-step-3-client-document" value={form.document} onChange={(e) => set("document", e.target.value)} className={fieldCls} placeholder="000.000.000-00" />
      </Field>
      <Field label="WhatsApp">
        <Input data-testid="checkout-step-3-client-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={fieldCls} placeholder="(27) 99999-9999" />
      </Field>
    </div>
    <Field label="E-mail">
      <Input data-testid="checkout-step-3-client-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} placeholder="voce@email.com" />
    </Field>
    <Field label="Observações (opcional)">
      <Textarea data-testid="checkout-step-3-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={`${fieldCls} h-auto py-3`} placeholder="Tipo de evento, tema, número de convidados..." />
    </Field>
  </div>
);
